import { RedReview } from 'models/RedReview';
import { AddonActionPayload } from 'views/ProductPage/addons/actions';
import { isInstallationConfigurator } from 'views/ProductPage/configurators/selector';
import { DetailedProduct } from 'models/DetailedProduct';
import { Action as ReduxAction } from 'redux';
import { ProductPageModel } from 'models/ProductPageModel';

export interface OwnState {
  loading: boolean;
  product?: ProductPageModel;
  variant?: ProductPageModel;
  addons?: ProductPageModel['addons'];
  reviews?: RedReview[];
  reviewsPage?: number;
  zipCode?: string;
}

export const DRAWER_PRODUCT_SET_PRODUCT = 'DRAWER_PRODUCT_SET_PRODUCT';
export type SetDrawerProductAction = ReduxAction & {
  product: DetailedProduct;
  addons?: ProductPageModel['addons'];
};
export const setDrawerProduct = (
  product: DetailedProduct,
  addons?: ProductPageModel['addons']
): SetDrawerProductAction => ({
  type: DRAWER_PRODUCT_SET_PRODUCT,
  product: product,
  addons
});

export type Action =
  | { type: 'setLoading' }
  | { type: 'setProduct'; product: ProductPageModel }
  | { type: 'setVariant'; variant: ProductPageModel }
  | { type: 'setReviews'; reviews: RedReview[]; pageNumber: number }
  | { type: 'setReviewHelpful'; id: string }
  | { type: 'resetVariant' }
  | { type: 'toggleAddon'; payload: AddonActionPayload; selected: boolean }
  | { type: 'setValue'; id: string; value: string }
  | { type: 'setZipCode'; value: string }
  | { type: 'setInstallation'; id?: string }
  | { type: 'toggleInstallationAddon'; id?: string };

export const reducer = (state: OwnState, action: Action): OwnState => {
  switch (action.type) {
    case 'setLoading':
      return { ...state, loading: true };
    case 'setProduct':
      return { ...state, loading: false, product: action.product };
    case 'setVariant':
      if (!state.variant) {
        return { ...state, loading: false, variant: action.variant, addons: action.variant.addons };
      }

      return { ...state, loading: false, variant: action.variant };
    case 'setReviews':
      return {
        ...state,
        reviews: state.reviews ? [...state.reviews, ...action.reviews] : action.reviews,
        reviewsPage: action.pageNumber
      };
    case 'setReviewHelpful':
      return {
        ...state,
        reviews: state.reviews?.map(review => {
          if (review.reviewId === action.id) {
            return {
              ...review,
              helpful: review.helpful + 1,
              hasVoted: true
            };
          }

          return review;
        })
      };
    case 'resetVariant':
      return { ...state, variant: undefined };
    case 'toggleAddon': {
      const { groupKey, productId } = action.payload;
      const selected = action.selected;

      const addons = state.addons;

      if (addons) {
        const addonGroup = addons[groupKey];

        addonGroup.products = addonGroup.products.map(p => {
          p.selected = false;

          if (p.productId === productId) {
            p.selected = !selected;
          }

          return p;
        });

        return { ...state, addons: { ...addons } };
      }

      return state;
    }
    case 'setValue': {
      if (!state.product?.configurator) {
        return state;
      }

      const { id, value } = action;

      const updatedAttributes = state.product.configurator.attributes.map(attribute => {
        if (attribute.id !== id) {
          return attribute;
        }
        const updatedValues = attribute.values.map(val => ({
          ...val,
          selected: val.value === value
        }));
        return { ...attribute, values: updatedValues };
      });

      return {
        ...state,
        product: {
          ...state.product,
          configurator: {
            ...state.product.configurator,
            attributes: updatedAttributes,
            values: {
              ...state.product.configurator.values,
              [id]: value
            }
          }
        }
      };
    }
    case 'setZipCode': {
      return {
        ...state,
        zipCode: action.value
      };
    }
    case 'setInstallation': {
      if (
        state.product &&
        state.product.configurator &&
        isInstallationConfigurator(state.product.configurator)
      ) {
        const newState = {
          ...state,
          product: state.product?.configurator && {
            ...state.product,
            configurator: {
              ...state.product.configurator,
              installation: {
                ...state.product.configurator.installation,
                products: state.product.configurator.installation.products.map(product => ({
                  ...product,
                  selected: product.productId === action.id
                }))
              }
            }
          }
        };
        return newState;
      }
      return state;
    }
    case 'toggleInstallationAddon': {
      if (
        state.product &&
        state.product.configurator &&
        isInstallationConfigurator(state.product.configurator) &&
        state.product.configurator.addons
      ) {
        const newState = {
          ...state,
          product: state.product?.configurator && {
            ...state.product,
            configurator: {
              ...state.product.configurator,
              addons: {
                ...state.product.configurator.addons,
                products: state.product.configurator.addons.products.map(product => ({
                  ...product,
                  selected: product.productId === action.id
                }))
              }
            }
          }
        };
        return newState;
      }
      return state;
    }
  }
};
